import { IAccountMappingTemplate, IAnnualReport, IFinStatements } from '@modeso/types__aumico-lib-annualreport-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import { cloneDeep } from 'lodash-es';
import {
    INotesLiveData,
    NotesLiveRefreshHelper,
} from '../../../../../../aumico-lib-notes-fe/src/lib/helpers/notes-live-refresh.helper';
import {
    IFxNodeStamp,
    IFxNotesContext,
} from '../../../../../../aumico-lib-notes-fe/src/lib/interfaces/IFxNotesContext.interface';
import { SuperTableMapper } from '../../../../../../aumico-lib-notes-fe/src/lib/mappers/super-table.mapper';
import { templateConstants } from '../../../shared/template-constants';
import { FlisMapper } from '../mappers/flis.mapper';
import { FxTranslationHelper } from '../utils/FxTranslationHelper';
import { GroupingHelper } from '../utils/GroupingHelper';

/** The templates the notes derive from: G&M-transformed for FLIs, raw for the super-table trees (REV-21023). */
export interface INotesLiveTemplates {
    balance: IAccountMappingTemplate;
    income: IAccountMappingTemplate;
    rawBalance: IAccountMappingTemplate;
    rawIncome: IAccountMappingTemplate;
}

/** The FX part of the recipe (REV-21512): the super-table context and the collector for the FLI walk. */
export interface INotesLiveFx {
    context: IFxNotesContext;
    fliFxStamps: Map<string, IFxNodeStamp>;
}

/**
 * REV-22548: the one recipe for the live data the notes are refreshed against. The notes page and
 * Abschluss & Druck both build it here, so the print warning can never judge the notes against
 * different numbers than the notes page shows.
 */
export class NotesLiveDataHelper {
    /**
     * Same transformation the print mappers apply. The raw templates are returned BY REFERENCE: the
     * notes page stamps FX values onto them in place.
     */
    static buildTemplates(annualReport: IAnnualReport, finStatements: IFinStatements[]): INotesLiveTemplates | null {
        const rawBalance = finStatements?.[templateConstants.balanceTemplate]?.templateAndAccountsMapping?.template;
        const rawIncome = finStatements?.[templateConstants.incomeTemplate]?.templateAndAccountsMapping?.template;
        if (rawBalance?.group == null || rawIncome?.group == null) {
            return null;
        }

        // Default to true to match print behavior - grouping & masking should always be applied
        const shouldApplyTransformations = annualReport.groupingAndMasking?.enabled ?? true;
        const transform = (template: IAccountMappingTemplate): IAccountMappingTemplate => ({
            ...template,
            group: GroupingHelper.applyAllGroupingMaskingTransformations(
                template.group,
                annualReport.groupingAndMasking,
                shouldApplyTransformations,
            ),
        });

        return { balance: transform(rawBalance), income: transform(rawIncome), rawBalance, rawIncome };
    }

    /**
     * FX reports only: stamps the FX values onto BOTH template shapes in place (raw for the
     * super-table trees, transformed for the FLI walk). Null when FX is off.
     */
    static applyFx(
        annualReport: IAnnualReport,
        finStatements: IFinStatements[],
        templates: INotesLiveTemplates,
        reserveLabel: string,
    ): INotesLiveFx | null {
        const fx = FxTranslationHelper.buildSuperTablePrintFx(annualReport, finStatements, reserveLabel);
        if (fx == null) {
            return null;
        }
        fx.stampTemplateGroup(templates.rawBalance.group, 'balance');
        fx.stampTemplateGroup(templates.rawIncome.group, 'income');
        fx.stampTemplateGroup(templates.balance.group, 'balance');
        fx.stampTemplateGroup(templates.income.group, 'income');
        return { context: fx.context, fliFxStamps: new Map() };
    }

    static buildLiveData(templates: INotesLiveTemplates, fx: INotesLiveFx | null = null): INotesLiveData {
        const superTableMapper = new SuperTableMapper();
        return {
            flis: new FlisMapper().getFlisOfBalanceAndIncomeTemplates(
                templates.balance,
                templates.income,
                fx?.fliFxStamps,
            ),
            balanceMappingItemsSelectionTree: superTableMapper.buildMappingItemsSelectionTree(
                templates.rawBalance.group,
                TemplateType.BALANCE,
                fx?.context ?? null,
            ),
            incomeMappingItemsSelectionTree: superTableMapper.buildMappingItemsSelectionTree(
                templates.rawIncome.group,
                TemplateType.INCOME,
            ),
        };
    }

    /**
     * True only when opening and saving the notes would change what they show — the content check
     * behind the print warning. A version number alone never decides it: G&M bumps the version on
     * any change, most of which never reach the notes.
     */
    static isSavedNotesOutOfDate(
        annualReport: IAnnualReport,
        finStatements: IFinStatements[],
        fxReserveLabel: string,
    ): boolean {
        const accordions = annualReport?.notes?.accordions;
        if (!Array.isArray(accordions) || accordions.length === 0) {
            return false;
        }
        // A clone: the store's statements may be frozen, and the mappers and FX stamp values in place.
        const statements = cloneDeep(finStatements);
        const templates = NotesLiveDataHelper.buildTemplates(annualReport, statements);
        if (templates == null) {
            return false;
        }
        const fx = NotesLiveDataHelper.applyFx(annualReport, statements, templates, fxReserveLabel);
        return NotesLiveRefreshHelper.isOutOfDate(accordions, NotesLiveDataHelper.buildLiveData(templates, fx));
    }
}
